import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { concatMap, first, map, Observable, of } from 'rxjs';
import { DateRangeDialogComponent, DateRangeDialogData, DateRangeDialogResult } from './date-range-dialog.component';
import { MessageBoxComponent, MessageBoxData } from './messagebox.component';
import { TextEditDialogComponent, TextEditDialogData, TextEditDialogResult } from './text-edit-dialog.component';
import { UnitOfWork } from 'app/core/services';
import { wrapIntoObservable } from 'app/core/utils/async-await';

@Injectable({ providedIn: 'root' })
export class MessageBoxService {
  constructor(private dialog: MatDialog) {}

  showMessage<T extends string>(title: string, message: string, actions?: T[]): Observable<T> {
    const data: MessageBoxData = {
      title,
      message: message.replace(/\n/g, '<br>'),
      actions: actions || ['Ok'],
    };

    const dialogRef = this.dialog.open(MessageBoxComponent, {
      width: '500px',
      data,
    });
    return dialogRef.afterClosed();
  }

  showTextEdit(data: TextEditDialogData): Observable<TextEditDialogResult> {
    const dialogRef = this.dialog.open(TextEditDialogComponent, {
      width: '500px',
      data,
    });
    return dialogRef.afterClosed();
  }

  showDateRange(data: DateRangeDialogData): Observable<DateRangeDialogResult> {
    const dialogRef = this.dialog.open(DateRangeDialogComponent, {
      width: '500px',
      data,
    });
    return dialogRef.afterClosed();
  }

  /** If there are changes, show the message box asking whether to discard them. 
   * @returns true to allow deactivate, false otherwise. */
  showCanDeactivate(hasChanges: boolean | Promise<boolean> | Observable<boolean>, discard: () => any): Observable<boolean> {
    const hasChanges$ = wrapIntoObservable(hasChanges);

    return hasChanges$.pipe(
      first(),
      concatMap((hasChanges) => {
        if (!hasChanges) { return of(true); }

        return this.showMessage('Unsaved changes', 'There are unsaved changes. Are you sure you want to discard them?', [ 'Yes', 'No', ])
          .pipe(
            map((response) => {
              if (response === 'Yes') {
                discard();
                return true;
              }
              return false;
            }),
          );
      }),
    );    
  }

}
